/**
 * Warnung, wenn die offenen Positionen nicht mehr zum Konto passen.
 *
 * Steht direkt unter „Was ist zu tun", weil sie dieselbe Frage von der
 * anderen Seite beantwortet: Die Handlungsliste sagt, was zu tun ist — diese
 * Karte sagt, wenn das Konto es nicht mehr trägt.
 *
 * Sie rendert NICHTS, solange alles passt. Anders als bei der Handlungsliste
 * wäre „heute nichts zu beanstanden" hier kein Ergebnis, sondern Rauschen:
 * Eine Warnung, die immer sichtbar ist, wird nach drei Tagen überlesen — und
 * dann fehlt sie genau an dem Tag, an dem sie zählt.
 */
import { AlertTriangle, Ruler } from 'lucide-react';

import type { AccountSummary } from '@/lib/paper-account';
import { pruefeKapital } from '@/lib/v6/kapitalpruefung';
import { cn } from '@/lib/utils';

interface Props {
  summary: AccountSummary;
  /** max_total_open_risk_pct aus den Einstellungen. */
  maxOffenesRisikoPct: number;
}

export function KapitalWarnungCard({ summary, maxOffenesRisikoPct }: Props) {
  const befunde = pruefeKapital({ summary, maxOffenesRisikoPct });
  if (befunde.length === 0) return null;

  // Ein gerechneter Befund wiegt schwerer als eine Faustregel und faerbt
  // deshalb die ganze Karte.
  const hartesProblem = befunde.some((b) => b.gerechnet);

  return (
    <div
      className={cn(
        'cockpit-card p-4 space-y-3',
        hartesProblem ? 'ring-1 ring-rose-500/40' : 'ring-1 ring-amber-500/30'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold">Passt nicht zum Konto</h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">
          gegen {summary.equity.toLocaleString('de-DE', { maximumFractionDigits: 0 })}{' '}
          {summary.account_currency}
        </span>
      </div>

      <ul className="space-y-2.5">
        {befunde.map((b) => (
          <li
            key={b.art}
            className={cn(
              'rounded border px-3 py-2.5 space-y-1',
              b.gerechnet
                ? 'border-rose-500/30 bg-rose-500/[0.06]'
                : 'border-amber-500/25 bg-amber-500/[0.05]'
            )}
          >
            <div className="flex items-center gap-2">
              {b.gerechnet ? (
                <AlertTriangle className="w-4 h-4 shrink-0 text-rose-300" />
              ) : (
                <Ruler className="w-4 h-4 shrink-0 text-amber-300" />
              )}
              <span className="text-[13px] font-medium">{b.satz}</span>
            </div>
            <p className="text-[11px] text-zinc-400 leading-snug pl-6">{b.erklaerung}</p>
          </li>
        ))}
      </ul>

      <p className="text-[10px] text-zinc-500 leading-snug border-t border-zinc-800/60 pt-2">
        Rote Punkte sind gerechnet — sie folgen aus den Zahlen, nicht aus einer
        Meinung. Gelbe sind Faustregeln, für die es hier keine Messung gibt;
        die darfst du überstimmen.
      </p>
    </div>
  );
}
